/**
 * Diálogos de **Ventas Crédito** (`/logistica/ventas-credito`).
 *
 * - `DetalleVentaCredito`: el modal de detalles del panel anterior (cliente,
 *   conductor, placa, kilometraje, adelanto y la línea del producto), más el
 *   estado del crédito y los enlaces pendientes del canje de factura externa.
 * - `EditarVentaCredito`: el formulario con los datos editables (placa del vehículo,
 *   DNI y nombre del conductor, vale y kilometraje). La placa se añade al formulario
 *   del panel anterior para poder corregirla después de emitida la venta.
 */

import { Ban, Check, Lock, Pencil } from 'lucide-react';
import { useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency, formatDateValue, formatNumber } from '@/lib/utils';

import type { VentaCreditoDetalle, VentaCreditoEditable } from '../ventas-credito.types';

/** Distintivo del estado del crédito, como el panel anterior. */
function EstadoBadge({ venta }: { venta: VentaCreditoDetalle }) {
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Badge variant={venta.activo ? 'success' : 'danger'}>
        {venta.activo ? 'Activo' : 'Anulado'}
      </Badge>
      {venta.canjeado ? <Badge variant="brand">Canjeado</Badge> : null}
      {venta.estado_sunat ? <Badge variant="neutral">{venta.estado_sunat}</Badge> : null}
      {venta.editado ? (
        <Badge variant="warning" title={`Editado el ${venta.actualizado ?? ''}`}>
          Editado
        </Badge>
      ) : null}
    </span>
  );
}

/** Fila de dato del detalle. */
function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <p className="text-sm text-ink-700">
      <span className="font-semibold text-ink-500">{etiqueta}: </span>
      {valor || '—'}
    </p>
  );
}

export function DetalleVentaCredito({
  venta,
  cargando,
  error,
  onEditar,
  onCerrar,
}: {
  venta: VentaCreditoDetalle | null;
  cargando: boolean;
  error: string | null;
  onEditar: () => void;
  onCerrar: () => void;
}) {
  return (
    <Dialog
      open
      onClose={onCerrar}
      size="md"
      title="Detalle de la venta a crédito"
      description={venta ? `${venta.serie}-${venta.numero}` : undefined}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar}>
            Cerrar
          </Button>
          {venta?.puede_editar ? (
            <Button
              size="sm"
              leftIcon={<Pencil className="size-3.5" aria-hidden="true" />}
              onClick={onEditar}
            >
              Editar
            </Button>
          ) : null}
        </>
      }
    >
      {cargando ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : error ? (
        <Alert variant="error" title="No se pudo leer el detalle">
          {error}
        </Alert>
      ) : venta ? (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            <EstadoBadge venta={venta} />
            <span className="text-sm font-semibold text-ink-900">
              {formatCurrency(venta.total)}
            </span>
          </div>

          <div className="rounded-control border border-ink-100 bg-ink-50/50 p-3">
            <Dato etiqueta="Cliente" valor={venta.cliente} />
            <Dato etiqueta="Documento" valor={venta.documento_cliente} />
            <Dato etiqueta="Dirección" valor={venta.direccion_cliente} />
            <Dato
              etiqueta="Conductor"
              valor={`${venta.conductor_nombre || '—'} ${venta.conductor_dni ? `(${venta.conductor_dni})` : ''}`}
            />
            <Dato etiqueta="Placa" valor={venta.placa} />
            <Dato
              etiqueta="Kilometraje"
              valor={venta.kilometraje === null ? '' : formatNumber(venta.kilometraje)}
            />
            <Dato etiqueta="Vale" valor={venta.vale} />
            <Dato etiqueta="Adelanto" valor={formatCurrency(venta.adelanto)} />
            <Dato etiqueta="Pagado" valor={formatCurrency(venta.pagado)} />
            <Dato etiqueta="Vendedor" valor={venta.vendedor} />
            <Dato etiqueta="Registrado" valor={venta.creado ?? ''} />
            {venta.vencimiento ? (
              <Dato etiqueta="Vencimiento" valor={formatDateValue(venta.vencimiento)} />
            ) : null}
          </div>

          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                <th className="py-2">Cant</th>
                <th className="py-2">Producto</th>
                <th className="py-2 text-right">P. Unit.</th>
                <th className="py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-2 text-ink-700">
                  {formatNumber(venta.cantidad)}
                  {venta.descuento > 0 ? (
                    <span className="block text-[11px] text-ink-400">
                      Desc. {formatCurrency(venta.descuento)}
                    </span>
                  ) : null}
                </td>
                <td className="py-2 text-ink-700">
                  {venta.producto}
                  {venta.unidad ? (
                    <span className="ml-1 text-[11px] text-ink-400">({venta.unidad})</span>
                  ) : null}
                  {venta.es_global ? (
                    <Badge variant="outline" className="ml-2">
                      Global
                    </Badge>
                  ) : null}
                </td>
                <td className="py-2 text-right text-ink-700">{formatCurrency(venta.precio)}</td>
                <td className="py-2 text-right font-semibold text-ink-900">
                  {formatCurrency(venta.total)}
                </td>
              </tr>
            </tbody>
          </table>

          {venta.puede_canje_externo ? (
            <Alert variant="info">
              <span className={ICONO_EN_ALERTA}>
                <Lock className="size-4" aria-hidden="true" />
                El <strong>canje de factura externa</strong> es otra pantalla del panel anterior, así
                que aún no está disponible aquí.
              </span>
            </Alert>
          ) : null}

          {venta.activo ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="ghost" size="sm" disabled title="Ticket · usa el circuito de impresión">
                <Lock className="mr-1 size-3.5" aria-hidden="true" /> Ticket
              </Button>
              {venta.puede_permutar ? (
                <Button variant="ghost" size="sm" disabled title="Permutar · es otra pantalla">
                  <Lock className="mr-1 size-3.5" aria-hidden="true" /> Permutar
                </Button>
              ) : null}
              <Button
                variant="ghost"
                size="sm"
                disabled
                title="Anular · anula además el comprobante en el controlador PTS"
              >
                <Ban className="mr-1 size-3.5" aria-hidden="true" /> Anular
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}

export function EditarVentaCredito({
  venta,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  venta: VentaCreditoDetalle;
  guardando: boolean;
  error: string | null;
  onGuardar: (datos: VentaCreditoEditable) => void;
  onCerrar: () => void;
}) {
  // El diálogo se monta cuando el detalle ya está cargado, y el listado lo
  // desmonta al cerrarlo, así que el formulario se inicializa una sola vez.
  const [datos, setDatos] = useState<VentaCreditoEditable>({
    conductor_dni: venta.conductor_dni ?? '',
    conductor_nombre: venta.conductor_nombre ?? '',
    placa: venta.placa ?? '',
    vale: venta.vale ?? '',
    kilometraje: venta.kilometraje === null ? '' : String(venta.kilometraje),
  });

  const dniInvalido = datos.conductor_dni !== '' && !/^\d{8}$/.test(datos.conductor_dni);
  const kilometrajeInvalido = datos.kilometraje !== '' && Number(datos.kilometraje) < 0;
  // Se admite cualquier combinación de mayúsculas y minúsculas: al guardar se pasa a
  // mayúsculas (hay placas antiguas guardadas en minúsculas).
  const placaInvalida = datos.placa !== '' && !/^[A-Za-z0-9 -]+$/.test(datos.placa);

  return (
    <Dialog
      open
      onClose={onCerrar}
      size="md"
      title="Editar la venta a crédito"
      description={`${venta.serie}-${venta.numero} · ${venta.cliente || 'cliente no informado'}`}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={dniInvalido || kilometrajeInvalido || placaInvalida}
            leftIcon={<Check className="size-3.5" aria-hidden="true" />}
            onClick={() => onGuardar(datos)}
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Alert variant="info">
          Se editan los datos del vehículo y del conductor, el vale y el kilometraje. El monto y el
          comprobante no cambian.
        </Alert>

        {error ? <Alert variant="error">{error}</Alert> : null}

        <Input
          label="Placa del vehículo"
          value={datos.placa}
          maxLength={15}
          placeholder="ABC-123"
          hint="Se guarda en mayúsculas; vacío si la venta no llevaba placa."
          error={placaInvalida ? 'Sólo letras, números, guion y espacio.' : undefined}
          onChange={(evento) =>
            setDatos({ ...datos, placa: evento.target.value.toUpperCase().slice(0, 15) })
          }
        />

        <Input
          label="DNI del conductor"
          value={datos.conductor_dni}
          inputMode="numeric"
          maxLength={8}
          placeholder="8 dígitos"
          error={dniInvalido ? 'El DNI debe tener 8 dígitos.' : undefined}
          onChange={(evento) => setDatos({ ...datos, conductor_dni: evento.target.value.trim() })}
        />

        <Input
          label="Nombre del conductor"
          value={datos.conductor_nombre}
          maxLength={255}
          onChange={(evento) => setDatos({ ...datos, conductor_nombre: evento.target.value })}
        />

        <Input
          label="Vale"
          value={datos.vale}
          maxLength={45}
          onChange={(evento) => setDatos({ ...datos, vale: evento.target.value })}
        />

        <Input
          label="Kilometraje"
          type="number"
          step="0.01"
          min={0}
          value={datos.kilometraje}
          error={kilometrajeInvalido ? 'El kilometraje no puede ser negativo.' : undefined}
          onChange={(evento) => setDatos({ ...datos, kilometraje: evento.target.value })}
        />
      </div>
    </Dialog>
  );
}
